import React, { useEffect, useState } from 'react';
import { Trophy, Medal, Crown, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card } from '../components/Card';
import { WebLayout } from '../components/WebLayout';
import { useAuth } from '../contexts/AuthContext';
import { db, isFirebaseEnabled } from '../lib/firebase';
import { collection, query, orderBy, limit, getDocs, where, Timestamp } from 'firebase/firestore';
type TimeFilter = 'week' | 'month' | 'allTime';
interface LeaderboardUser {
  id: string;
  name: string;
  avatar: string;
  xp: number;
  level: number;
  completedGoals: number;
  rank: number;
}
export function LeaderboardPage() {
  const {
    user
  } = useAuth();
  const [filter, setFilter] = useState<TimeFilter>('week');
  const [leaderboard, setLeaderboard] = useState<LeaderboardUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState<number | null>(null);
  useEffect(() => {
    fetchLeaderboard();
  }, [filter]);
  const fetchLeaderboard = async () => {
    setLoading(true);
    if (!isFirebaseEnabled || !db) {
      // Mock data with different values for each time period
      const mockData: Record<TimeFilter, LeaderboardUser[]> = {
        week: [{
          id: '1',
          name: 'Sarah Chen',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah',
          xp: 2450,
          level: 12,
          completedGoals: 28,
          rank: 1
        }, {
          id: '2',
          name: 'Marcus Johnson',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Marcus',
          xp: 2180,
          level: 11,
          completedGoals: 24,
          rank: 2
        }, {
          id: '3',
          name: 'Emma Wilson',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Emma',
          xp: 1920,
          level: 10,
          completedGoals: 21,
          rank: 3
        }, {
          id: '4',
          name: 'Alex Rodriguez',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alex',
          xp: 1750,
          level: 9,
          completedGoals: 19,
          rank: 4
        }, {
          id: '5',
          name: 'Olivia Brown',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Olivia',
          xp: 1580,
          level: 9,
          completedGoals: 17,
          rank: 5
        }],
        month: [{
          id: '1',
          name: 'Emma Wilson',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Emma',
          xp: 8920,
          level: 15,
          completedGoals: 89,
          rank: 1
        }, {
          id: '2',
          name: 'Sarah Chen',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah',
          xp: 8450,
          level: 14,
          completedGoals: 84,
          rank: 2
        }, {
          id: '3',
          name: 'Marcus Johnson',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Marcus',
          xp: 7680,
          level: 13,
          completedGoals: 76,
          rank: 3
        }, {
          id: '4',
          name: 'James Lee',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=James',
          xp: 7250,
          level: 13,
          completedGoals: 72,
          rank: 4
        }, {
          id: '5',
          name: 'Sophia Martinez',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sophia',
          xp: 6890,
          level: 12,
          completedGoals: 68,
          rank: 5
        }],
        allTime: [{
          id: '1',
          name: 'Marcus Johnson',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Marcus',
          xp: 24580,
          level: 25,
          completedGoals: 245,
          rank: 1
        }, {
          id: '2',
          name: 'Emma Wilson',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Emma',
          xp: 22920,
          level: 23,
          completedGoals: 229,
          rank: 2
        }, {
          id: '3',
          name: 'Sarah Chen',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah',
          xp: 21450,
          level: 22,
          completedGoals: 214,
          rank: 3
        }, {
          id: '4',
          name: 'David Kim',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=David',
          xp: 19680,
          level: 20,
          completedGoals: 196,
          rank: 4
        }, {
          id: '5',
          name: 'Isabella Garcia',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Isabella',
          xp: 18250,
          level: 19,
          completedGoals: 182,
          rank: 5
        }]
      };
      setLeaderboard(mockData[filter]);
      setUserRank(Math.floor(Math.random() * 50) + 10); // Random rank between 10-60
      setLoading(false);
      return;
    }
    try {
      // Calculate date range based on filter
      let startDate: Date | null = null;
      if (filter === 'week') {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);
      } else if (filter === 'month') {
        startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 1);
      }
      // Query users based on time period
      const usersRef = collection(db, 'users');
      let q = query(usersRef, orderBy('xp', 'desc'), limit(10));
      if (startDate) {
        // For week/month, we'd need to aggregate goals completed in that period
        // This is a simplified version - in production, you'd have a separate leaderboard collection
        q = query(usersRef, where('lastActive', '>=', Timestamp.fromDate(startDate)), orderBy('lastActive', 'desc'), orderBy('xp', 'desc'), limit(10));
      }
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map((doc, index) => ({
        id: doc.id,
        name: doc.data().displayName || 'Anonymous',
        avatar: doc.data().photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${doc.id}`,
        xp: doc.data().xp || 0,
        level: doc.data().level || 1,
        completedGoals: doc.data().completedGoals || 0,
        rank: index + 1
      }));
      setLeaderboard(users);
      // Find current user's rank
      const currentUserIndex = users.findIndex(u => u.id === user?.uid);
      setUserRank(currentUserIndex >= 0 ? currentUserIndex + 1 : null);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };
  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-game-yellow" data-id="element-948" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" data-id="element-949" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-orange-400" data-id="element-950" />;
    return <span className="font-fredoka font-bold text-gray-500 dark:text-gray-400" data-id="element-951">
        #{rank}
      </span>;
  };
  const getRankBadge = (rank: number) => {
    if (rank === 1) return 'bg-gradient-to-r from-game-yellow to-game-coral';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-400';
    if (rank === 3) return 'bg-gradient-to-r from-orange-300 to-orange-400';
    return 'bg-gray-100 dark:bg-gray-800';
  };
  return <WebLayout data-id="element-952">
      <header className="px-6 lg:px-8 pt-6 lg:pt-8 pb-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#252936]" data-id="element-953">
        <div className="max-w-4xl mx-auto" data-id="element-954">
          <div className="flex items-center gap-3 mb-6" data-id="element-955">
            <div className="w-12 h-12 bg-game-yellow rounded-2xl flex items-center justify-center" data-id="element-956">
              <Trophy className="w-6 h-6 text-white" data-id="element-957" />
            </div>
            <div data-id="element-958">
              <h1 className="text-3xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-959">
                Leaderboard
              </h1>
              <p className="text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-960">
                Compete with top performers
              </p>
            </div>
          </div>

          {/* Time Filter */}
          <div className="flex gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl" data-id="element-961">
            <button onClick={() => setFilter('week')} className={`flex-1 py-2 px-4 rounded-xl font-fredoka font-bold text-sm transition-all ${filter === 'week' ? 'bg-white dark:bg-[#252936] text-game-purple shadow-sm' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-962">
              This Week
            </button>
            <button onClick={() => setFilter('month')} className={`flex-1 py-2 px-4 rounded-xl font-fredoka font-bold text-sm transition-all ${filter === 'month' ? 'bg-white dark:bg-[#252936] text-game-purple shadow-sm' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-963">
              This Month
            </button>
            <button onClick={() => setFilter('allTime')} className={`flex-1 py-2 px-4 rounded-xl font-fredoka font-bold text-sm transition-all ${filter === 'allTime' ? 'bg-white dark:bg-[#252936] text-game-purple shadow-sm' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-964">
              All Time
            </button>
          </div>
        </div>
      </header>

      <main className="px-6 lg:px-8 py-6 lg:py-8" data-id="element-965">
        <div className="max-w-4xl mx-auto space-y-4" data-id="element-966">
          {loading ? <div className="flex items-center justify-center py-12" data-id="element-967">
              <div className="w-16 h-16 border-4 border-game-purple border-t-transparent rounded-full animate-spin" data-id="element-968"></div>
            </div> : <>
              {/* Current User Rank */}
              {userRank && <Card className="p-4 bg-gradient-to-r from-game-purple/10 to-game-teal/10 border-2 border-game-purple/30" data-id="element-969">
                  <div className="flex items-center justify-between" data-id="element-970">
                    <div className="flex items-center gap-3" data-id="element-971">
                      <TrendingUp className="w-5 h-5 text-game-purple" data-id="element-972" />
                      <span className="font-fredoka font-bold text-game-dark dark:text-white" data-id="element-973">
                        Your Rank
                      </span>
                    </div>
                    <span className="font-fredoka font-bold text-2xl text-game-purple" data-id="element-974">
                      #{userRank}
                    </span>
                  </div>
                </Card>}

              {/* Leaderboard List */}
              {leaderboard.map((player, index) => <motion.div key={player.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05
          }} data-id="element-975">
                  <Card className={`p-4 ${player.id === user?.uid ? 'ring-2 ring-game-purple' : ''}`} data-id="element-976">
                    <div className="flex items-center gap-4" data-id="element-977">
                      {/* Rank */}
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getRankBadge(player.rank)}`} data-id="element-978">
                        {getRankIcon(player.rank)}
                      </div>

                      {/* Avatar */}
                      <img src={player.avatar} alt={player.name} className="w-12 h-12 rounded-full border-2 border-gray-200 dark:border-gray-700" data-id="element-979" />

                      {/* User Info */}
                      <div className="flex-1 min-w-0" data-id="element-980">
                        <h3 className="font-fredoka font-bold text-game-dark dark:text-white truncate" data-id="element-981">
                          {player.name}
                          {player.id === user?.uid && <span className="ml-2 text-xs text-game-purple" data-id="element-982">
                              (You)
                            </span>}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-983">
                          Level {player.level} • {player.completedGoals} quests
                        </p>
                      </div>

                      {/* XP */}
                      <div className="text-right" data-id="element-984">
                        <p className="font-fredoka font-bold text-xl text-game-purple" data-id="element-985">
                          {player.xp.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-986">
                          XP
                        </p>
                      </div>
                    </div>
                  </Card>
                </motion.div>)}
            </>}
        </div>
      </main>
    </WebLayout>;
}